/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnChanges, OnInit,
} from '@angular/core';
import { ActivityService } from '../../../activity-feed/activity.service';
import { ActivityContext, ActivityView, toActivityView } from '../../../activity-feed/activity-view';
import { DashboardService } from '../shared/dashboard.service';

export type ActivityTab = 'all' | 'projects' | 'invitations' | 'community';

export const ACTIVITY_TABS: { id: ActivityTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'projects', label: 'Projects' },
  { id: 'invitations', label: 'Invitations' },
  { id: 'community', label: 'Community' },
];

// The Projects and Invitations tabs list this many, then link to the rest in the tester app.
export const PROJECT_LIST_LIMIT = 5;

// Drops a mention that sits next to a comment on the same item, so one reply isn't listed twice, as the
// AngularJS ActivityService.combine did.
export function combineMentions(items: any[]): any[] {
  return items.filter((item, i) => item.type !== 'at_mention_activity'
    || ![items[i - 1], items[i + 1]].some((n) => n?.type === 'comment_activity' && n.item?.id === item.item?.id));
}

// The Home page's My Activity card: All and Community activity, plus the tester's projects and invitations.
@Component({
  selector: 'ut-dashboard-activity-card',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard-activity-card.component.html',
  styleUrls: ['./dashboard-activity-card.component.scss'],
})
export class DashboardActivityCardComponent implements OnInit, OnChanges {
  @Input() testerAppUrl = '';

  readonly tabs = ACTIVITY_TABS;
  readonly limit = PROJECT_LIST_LIMIT;

  tab: ActivityTab = 'all';
  // undefined while loading, so the empty states don't flash.
  activities: ActivityView[] | undefined;
  community: ActivityView[] | undefined;
  projects: any[] | undefined;
  projectsCount = 0;
  invitations: any[] | undefined;
  invitationsCount = 0;

  private rawActivities: any[] | undefined;
  private rawCommunity: any[] | undefined;
  private context: ActivityContext;

  constructor(
    @Inject('UserService') private UserService: any,
    @Inject('GamificationService') GamificationService: any,
    private activityService: ActivityService,
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {
    this.context = { testerAppUrl: '', badgesById: GamificationService.badgesById || {} };
  }

  ngOnChanges(): void {
    this.context = { ...this.context, testerAppUrl: this.testerAppUrl };
    this.refresh();
  }

  ngOnInit(): void {
    this.activityService.feed({}).then((res) => {
      this.rawActivities = combineMentions(res.data || []);
      this.refresh();
    }, () => {
      this.rawActivities = [];
      this.refresh();
    });
    this.activityService.communityActivities({}).then((res) => {
      this.rawCommunity = combineMentions(res.data || []);
      this.refresh();
    }, () => {
      this.rawCommunity = [];
      this.refresh();
    });
    this.dashboardService.projects().then((res) => {
      this.projects = res.data || [];
      this.projectsCount = res.count || 0;
      this.cdr.markForCheck();
    }, () => this.empty('projects'));
    this.dashboardService.invitations().then((res) => {
      this.invitations = res.data || [];
      this.invitationsCount = res.count || 0;
      this.cdr.markForCheck();
    }, () => this.empty('invitations'));
    // Activity stays unread until the tester opens /activity, as with the 2026 /activity page's compact card.
    this.UserService.me().then((res) => {
      this.context = { ...this.context, currentUserId: res?.data?.id };
      this.refresh();
    }, () => {
      // Comment rows fall back to "a" / "an" instead of "your".
    });
  }

  tabId(id: ActivityTab): string {
    return `dashboardActivityTab-${id}`;
  }

  panelId(id: ActivityTab): string {
    return `dashboardActivityPanel-${id}`;
  }

  count(id: ActivityTab): number | undefined {
    if (id === 'projects') return this.projectsCount;
    if (id === 'invitations') return this.invitationsCount;
    return undefined;
  }

  select(id: ActivityTab): void {
    if (id === this.tab) return;
    this.tab = id;
    // Opening a tab marks its activity read on the server, as the AngularJS card did.
    if (id === 'projects') this.dashboardService.clearProjectActivities().catch(() => {});
    if (id === 'community') this.dashboardService.clearCommunityActivities().catch(() => {});
  }

  // Arrow keys, Home and End move between tabs (APG tabs pattern, automatic activation).
  onTabKeydown(event: KeyboardEvent): void {
    const index = this.tabs.findIndex((t) => t.id === this.tab);
    const last = this.tabs.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(this.tabs[next].id);
    this.host.nativeElement.querySelector<HTMLElement>(`#${this.tabId(this.tabs[next].id)}`)?.focus();
  }

  onPick(value: string): void {
    this.select(value as ActivityTab);
  }

  private empty(list: 'projects' | 'invitations'): void {
    this[list] = [];
    this.cdr.markForCheck();
  }

  // Rebuilt only when the items or context change, so rows keep their identity between checks.
  private refresh(): void {
    const toViews = (items: any[]) => items.map((a) => toActivityView(a, this.context)).filter(Boolean);
    this.activities = this.rawActivities && toViews(this.rawActivities);
    this.community = this.rawCommunity && toViews(this.rawCommunity);
    this.cdr.markForCheck();
  }
}
